import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Supplier Management Page (Level 5.2)
 *
 * Admin page for managing suppliers (CRUD operations)
 * Also serves as API testing tool for Level 4.1
 */
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import apiClient from '../../utils/apiClient';
import { notify } from '../../utils/notifications';
import { SUPPLIER_STATUS_LABELS, SUPPLIER_TYPE_LABELS, SUPPLIER_STATUS_COLORS } from '../../types/supplier';
import TabPageLayout from './FinancialHub/TabPageLayout';
import { Button, StatsGrid, StatCard, StatLabel, StatValue, TableContainer, Table, Th, Td, FiltersContainer, FilterGroup, Label, Input, Select, EmptyState, EmptyStateIcon, EmptyStateText } from './FinancialHub/SharedComponents';
const SupplierManagement = () => {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [statistics, setStatistics] = useState(null);
    // Filters
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    // Form state for add/edit
    const [formData, setFormData] = useState({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        fax: '',
        mobilePhone: '',
        website: '',
        address: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: 'USA'
        },
        supplierCode: '',
        supplierType: 'wholesaler',
        taxId: '',
        paymentTerms: 'Net 30',
        notes: '',
        tags: []
    });
    // Fetch suppliers
    const fetchSuppliers = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter)
                params.append('status', statusFilter);
            if (typeFilter)
                params.append('supplierType', typeFilter);
            if (searchQuery)
                params.append('search', searchQuery);
            const queryString = params.toString();
            const url = `/suppliers-list${queryString ? '?' + queryString : ''}`;
            const response = await apiClient.get(url);
            if (response.ok && response.data) {
                setSuppliers(response.data.suppliers || []);
                setStatistics(response.data.statistics);
            }
            else {
                notify.error('Error', response.error || 'Failed to load suppliers');
            }
        }
        catch (err) {
            notify.error('Error', err.message || 'Failed to fetch suppliers');
        }
        finally {
            setLoading(false);
        }
    };
    // Create supplier
    const createSupplier = async () => {
        setLoading(true);
        try {
            const response = await apiClient.post('/suppliers-create', formData);
            if (response.ok && response.data) {
                notify.success('Success', `Supplier created: ${response.data.supplier.supplierCode}`);
                setShowAddModal(false);
                resetForm();
                fetchSuppliers();
            }
            else {
                notify.error('Error', response.error || 'Failed to create supplier');
            }
        }
        catch (err) {
            notify.error('Error', err.message || 'Failed to create supplier');
        }
        finally {
            setLoading(false);
        }
    };
    // Update supplier
    const updateSupplier = async () => {
        if (!selectedSupplier)
            return;
        setLoading(true);
        try {
            const response = await apiClient.put('/suppliers-update', {
                id: selectedSupplier.id,
                ...formData
            });
            if (response.ok && response.data) {
                notify.success('Success', `Supplier updated: ${response.data.supplier.supplierCode}`);
                setSelectedSupplier(null);
                resetForm();
                fetchSuppliers();
            }
            else {
                notify.error('Error', response.error || 'Failed to update supplier');
            }
        }
        catch (err) {
            notify.error('Error', err.message || 'Failed to update supplier');
        }
        finally {
            setLoading(false);
        }
    };
    // Delete supplier
    const deleteSupplier = async (supplierId) => {
        if (!window.confirm('Are you sure? This will soft delete the supplier (set status to inactive).')) {
            return;
        }
        setLoading(true);
        try {
            const response = await apiClient.delete(`/suppliers-delete?id=${supplierId}`);
            if (response.ok) {
                notify.success('Success', 'Supplier deleted (soft delete)');
                fetchSuppliers();
            }
            else {
                notify.error('Error', response.error || 'Failed to delete supplier');
            }
        }
        catch (err) {
            notify.error('Error', err.message || 'Failed to delete supplier');
        }
        finally {
            setLoading(false);
        }
    };
    // View supplier details
    const viewSupplier = async (supplierId) => {
        setLoading(true);
        try {
            const response = await apiClient.get(`/suppliers-get?id=${supplierId}`);
            if (response.ok && response.data) {
                const supplier = response.data.supplier;
                setSelectedSupplier(supplier);
                setFormData({
                    companyName: supplier.companyName,
                    contactPerson: supplier.contactPerson,
                    email: supplier.email,
                    phone: supplier.phone,
                    fax: supplier.fax || '',
                    mobilePhone: supplier.mobilePhone || '',
                    website: supplier.website || '',
                    address: supplier.address,
                    supplierCode: supplier.supplierCode,
                    supplierType: supplier.supplierType,
                    taxId: supplier.taxId || '',
                    paymentTerms: supplier.paymentTerms,
                    notes: supplier.notes || '',
                    tags: supplier.tags || []
                });
            }
            else {
                notify.error('Error', response.error || 'Failed to load supplier');
            }
        }
        catch (err) {
            notify.error('Error', err.message || 'Failed to load supplier');
        }
        finally {
            setLoading(false);
        }
    };
    // Reset form
    const resetForm = () => {
        setFormData({
            companyName: '',
            contactPerson: '',
            email: '',
            phone: '',
            fax: '',
            mobilePhone: '',
            website: '',
            address: {
                street: '',
                city: '',
                state: '',
                zipCode: '',
                country: 'USA'
            },
            supplierCode: '',
            supplierType: 'wholesaler',
            taxId: '',
            paymentTerms: 'Net 30',
            notes: '',
            tags: []
        });
    };
    // Load suppliers on mount
    useEffect(() => {
        fetchSuppliers();
    }, []);
    // Stats Section
    const statsSection = statistics && (_jsxs(StatsGrid, { children: [_jsxs(StatCard, { children: [_jsx(StatLabel, { children: "Total Suppliers" }), _jsx(StatValue, { children: statistics.total })] }), _jsxs(StatCard, { children: [_jsx(StatLabel, { children: "Active" }), _jsx(StatValue, { style: { color: '#10b981' }, children: statistics.byStatus.active || 0 })] }), _jsxs(StatCard, { children: [_jsx(StatLabel, { children: "Total Purchases" }), _jsxs(StatValue, { children: ["$", statistics.totalPurchaseValue?.toLocaleString() || 0] })] }), _jsxs(StatCard, { children: [_jsx(StatLabel, { children: "Total Owed" }), _jsxs(StatValue, { style: { color: '#ef4444' }, children: ["$", statistics.totalOwed?.toLocaleString() || 0] })] })] }));
    // Actions Section
    const actionsSection = (_jsxs(_Fragment, { children: [_jsx(Button, { onClick: () => setShowAddModal(true), "$primary": true, children: "+ Add Supplier" }), _jsx(Button, { onClick: fetchSuppliers, disabled: loading, children: loading ? 'Loading...' : 'Refresh' })] }));
    // Filters Section
    const filtersSection = (_jsxs(FiltersContainer, { children: [_jsxs(FilterGroup, { children: [_jsx(Label, { children: "Search" }), _jsx(Input, { type: "text", placeholder: "Company name, code, or email...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value) })] }), _jsxs(FilterGroup, { children: [_jsx(Label, { children: "Status" }), _jsxs(Select, { value: statusFilter, onChange: (e) => setStatusFilter(e.target.value), children: [_jsx("option", { value: "", children: "All Statuses" }), _jsx("option", { value: "active", children: "Active" }), _jsx("option", { value: "inactive", children: "Inactive" }), _jsx("option", { value: "suspended", children: "Suspended" })] })] }), _jsxs(FilterGroup, { children: [_jsx(Label, { children: "Type" }), _jsxs(Select, { value: typeFilter, onChange: (e) => setTypeFilter(e.target.value), children: [_jsx("option", { value: "", children: "All Types" }), _jsx("option", { value: "manufacturer", children: "Manufacturer" }), _jsx("option", { value: "wholesaler", children: "Wholesaler" }), _jsx("option", { value: "broker", children: "Broker" }), _jsx("option", { value: "other", children: "Other" })] })] }), _jsx(FilterGroup, { children: _jsx(Button, { onClick: fetchSuppliers, disabled: loading, children: "Apply Filters" }) })] }));
    return (_jsxs(TabPageLayout, { title: "Supplier Management", subtitle: "Manage diamond suppliers and track purchases (Level 4.1 API Testing)", stats: statsSection, actions: actionsSection, filters: filtersSection, children: [_jsxs(TableContainer, { children: [_jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Code" }), _jsx(Th, { children: "Company Name" }), _jsx(Th, { children: "Contact Person" }), _jsx(Th, { children: "Email" }), _jsx(Th, { children: "Phone" }), _jsx(Th, { children: "Type" }), _jsx(Th, { children: "Total Purchases" }), _jsx(Th, { children: "Amount Owed" }), _jsx(Th, { children: "Status" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: suppliers.map((supplier) => (_jsxs("tr", { children: [_jsx(Td, { children: _jsx(Code, { children: supplier.supplierCode }) }), _jsx(Td, { children: _jsx("strong", { children: supplier.companyName }) }), _jsx(Td, { children: supplier.contactPerson }), _jsx(Td, { children: _jsx(Email, { children: supplier.email }) }), _jsx(Td, { children: supplier.phone }), _jsx(Td, { children: _jsx(Badge, { color: "#3b82f6", children: SUPPLIER_TYPE_LABELS[supplier.supplierType] }) }), _jsxs(Td, { children: ["$", supplier.totalPurchases?.toLocaleString() || 0] }), _jsxs(Td, { className: supplier.totalOwed > 0 ? 'owed' : '', children: ["$", supplier.totalOwed?.toLocaleString() || 0] }), _jsx(Td, { children: _jsx(Badge, { color: SUPPLIER_STATUS_COLORS[supplier.status], children: SUPPLIER_STATUS_LABELS[supplier.status] }) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [_jsx(ActionButton, { onClick: () => viewSupplier(supplier.id), children: "View/Edit" }), _jsx(ActionButton, { onClick: () => deleteSupplier(supplier.id), danger: true, children: "Delete" })] }) })] }, supplier.id))) })] }), suppliers.length === 0 && !loading && (_jsxs(EmptyState, { children: [_jsx(EmptyStateIcon, {}), _jsx("h3", { children: "No suppliers found" }), _jsx(EmptyStateText, { children: "Click \"Add Supplier\" to create your first supplier" })] }))] }), (showAddModal || selectedSupplier) && (_jsx(Modal, { onClick: (e) => {
                    if (e.target === e.currentTarget) {
                        setShowAddModal(false);
                        setSelectedSupplier(null);
                        resetForm();
                    }
                }, children: _jsxs(ModalContent, { children: [_jsxs(ModalHeader, { children: [_jsx(ModalTitle, { children: selectedSupplier ? 'Edit Supplier' : 'Add New Supplier' }), _jsx(CloseButton, { onClick: () => {
                                        setShowAddModal(false);
                                        setSelectedSupplier(null);
                                        resetForm();
                                    }, children: "\u00D7" })] }), _jsx(ModalBody, { children: _jsxs(FormGrid, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "Company Name *" }), _jsx(Input, { type: "text", value: formData.companyName, onChange: (e) => setFormData({ ...formData, companyName: e.target.value }), placeholder: "Diamond Wholesalers Inc" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Supplier Code *" }), _jsx(Input, { type: "text", value: formData.supplierCode, onChange: (e) => setFormData({ ...formData, supplierCode: e.target.value.toUpperCase() }), placeholder: "DIA001" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Contact Person *" }), _jsx(Input, { type: "text", value: formData.contactPerson, onChange: (e) => setFormData({ ...formData, contactPerson: e.target.value }), placeholder: "John Smith" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Supplier Type" }), _jsxs(Select, { value: formData.supplierType, onChange: (e) => setFormData({ ...formData, supplierType: e.target.value }), children: [_jsx("option", { value: "manufacturer", children: "Manufacturer" }), _jsx("option", { value: "wholesaler", children: "Wholesaler" }), _jsx("option", { value: "broker", children: "Broker" }), _jsx("option", { value: "other", children: "Other" })] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Email *" }), _jsx(Input, { type: "email", value: formData.email, onChange: (e) => setFormData({ ...formData, email: e.target.value }), placeholder: "contact@supplier.com" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Phone *" }), _jsx(Input, { type: "tel", value: formData.phone, onChange: (e) => setFormData({ ...formData, phone: e.target.value }), placeholder: "212-555-0100" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Fax" }), _jsx(Input, { type: "tel", value: formData.fax, onChange: (e) => setFormData({ ...formData, fax: e.target.value }), placeholder: "212-555-0101" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Mobile" }), _jsx(Input, { type: "tel", value: formData.mobilePhone, onChange: (e) => setFormData({ ...formData, mobilePhone: e.target.value }), placeholder: "917-555-0100" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Website" }), _jsx(Input, { type: "url", value: formData.website, onChange: (e) => setFormData({ ...formData, website: e.target.value }), placeholder: "https://supplier.com" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Tax ID" }), _jsx(Input, { type: "text", value: formData.taxId, onChange: (e) => setFormData({ ...formData, taxId: e.target.value }), placeholder: "12-3456789" })] }), _jsxs(FormGroup, { fullWidth: true, children: [_jsx(Label, { children: "Street Address *" }), _jsx(Input, { type: "text", value: formData.address.street, onChange: (e) => setFormData({
                                                    ...formData,
                                                    address: { ...formData.address, street: e.target.value }
                                                }), placeholder: "123 Diamond Street" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "City *" }), _jsx(Input, { type: "text", value: formData.address.city, onChange: (e) => setFormData({
                                                    ...formData,
                                                    address: { ...formData.address, city: e.target.value }
                                                }), placeholder: "New York" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "State *" }), _jsx(Input, { type: "text", value: formData.address.state, onChange: (e) => setFormData({
                                                    ...formData,
                                                    address: { ...formData.address, state: e.target.value }
                                                }), placeholder: "NY" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Zip Code *" }), _jsx(Input, { type: "text", value: formData.address.zipCode, onChange: (e) => setFormData({
                                                    ...formData,
                                                    address: { ...formData.address, zipCode: e.target.value }
                                                }), placeholder: "10001" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Country *" }), _jsx(Input, { type: "text", value: formData.address.country, onChange: (e) => setFormData({
                                                    ...formData,
                                                    address: { ...formData.address, country: e.target.value }
                                                }), placeholder: "USA" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Payment Terms" }), _jsxs(Select, { value: formData.paymentTerms, onChange: (e) => setFormData({ ...formData, paymentTerms: e.target.value }), children: [_jsx("option", { value: "COD", children: "COD (Cash on Delivery)" }), _jsx("option", { value: "Net 15", children: "Net 15" }), _jsx("option", { value: "Net 30", children: "Net 30" }), _jsx("option", { value: "Net 60", children: "Net 60" }), _jsx("option", { value: "Net 90", children: "Net 90" })] })] }), _jsxs(FormGroup, { fullWidth: true, children: [_jsx(Label, { children: "Notes" }), _jsx(Textarea, { value: formData.notes, onChange: (e) => setFormData({ ...formData, notes: e.target.value }), placeholder: "Additional notes about this supplier...", rows: 3 })] })] }) }), _jsxs(ModalFooter, { children: [_jsx(Button, { onClick: () => {
                                        setShowAddModal(false);
                                        setSelectedSupplier(null);
                                        resetForm();
                                    }, children: "Cancel" }), _jsx(Button, { onClick: selectedSupplier ? updateSupplier : createSupplier, "$primary": true, disabled: loading || !formData.companyName || !formData.contactPerson || !formData.email || !formData.phone || !formData.supplierCode, children: loading ? 'Saving...' : (selectedSupplier ? 'Update Supplier' : 'Create Supplier') })] })] }) }))] }));
};
// Component-specific styled components (not in SharedComponents)
const Code = styled.code `
    background: #f3f4f6;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.8rem;
`;
const Email = styled.span `
    color: #3b82f6;
`;
const Badge = styled.span `
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 500;
    background: ${props => props.color || '#e5e7eb'}22;
    color: ${props => props.color || '#6b7280'};
    border: 1px solid ${props => props.color || '#6b7280'}44;
`;
const ActionButtons = styled.div `
    display: flex;
    gap: 0.5rem;
`;
const ActionButton = styled.button `
    padding: 0.375rem 0.75rem;
    border-radius: 4px;
    border: 1px solid ${props => props.danger ? '#ef4444' : '#d1d5db'};
    background: ${props => props.danger ? '#fee' : 'white'};
    color: ${props => props.danger ? '#dc2626' : '#374151'};
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: ${props => props.danger ? '#fcc' : '#f9fafb'};
    }
`;
const Textarea = styled.textarea `
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color, #d1d5db);
    border-radius: 6px;
    font-size: 0.875rem;
    background: var(--input-bg, white);
    color: var(--text-primary, #1a1a1a);
    resize: vertical;
    font-family: inherit;

    &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
`;
const Modal = styled.div `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 2rem;
`;
const ModalContent = styled.div `
    background: var(--card-bg, white);
    border-radius: 12px;
    width: 100%;
    max-width: 800px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;
const ModalHeader = styled.div `
    padding: 1.5rem;
    border-bottom: 1px solid var(--border-color, #e5e7eb);
    display: flex;
    justify-content: space-between;
    align-items: center;
`;
const ModalTitle = styled.h2 `
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--text-primary, #1a1a1a);
    margin: 0;
`;
const CloseButton = styled.button `
    background: transparent;
    border: none;
    font-size: 1.5rem;
    color: var(--text-secondary, #666);
    cursor: pointer;
    padding: 0.5rem;
    line-height: 1;
    transition: color 0.2s;

    &:hover {
        color: var(--text-primary, #1a1a1a);
    }
`;
const ModalBody = styled.div `
    padding: 1.5rem;
    overflow-y: auto;
    flex: 1;
`;
const ModalFooter = styled.div `
    padding: 1.5rem;
    border-top: 1px solid var(--border-color, #e5e7eb);
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
`;
const FormGrid = styled.div `
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
`;
const FormGroup = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    grid-column: ${props => props.fullWidth ? '1 / -1' : 'auto'};
`;
export default SupplierManagement;
